import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { LitElement, html, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { BreachPathDetailsTransitionSideMenuDataType } from '../types';
import {
  headerIconRenderer,
  sourceResourceNameRenderer,
  sourceResourceTypeRenderer,
  targetResourceNameRenderer,
  targetResourceTypeRenderer,
} from '../utils/customDataRenderer';

/**
 * @summary Breach Path details transition side menu web component
 * @tag breach-path-details-transition-side-menu
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('breach-path-details-transition-side-menu')
export class BreachPathDetailsTransitionSideMenuWebComponent extends LitElement {
  @property({ type: Object, attribute: false })
  data: BreachPathDetailsTransitionSideMenuDataType = {} as BreachPathDetailsTransitionSideMenuDataType;

  @property({ type: Boolean, attribute: false })
  loading = false;

  static get styles() {
    return styles;
  }

  render(): TemplateResult {
    if (this.loading) {
      return this.renderLoading();
    }

    return html`
      <div class="header mt-6">
        ${this.renderHeaderSection()}
      </div>
      <div class="content">
      </div>
    `;
  }

  private renderLoading(): TemplateResult {
    return html`
      <gz-loader rows="5" style="padding-right: 16px; padding-top: 48px;"></gz-loader>
    `;
  }

  private renderHeaderSection(): TemplateResult {
    return html`
      <div class="header-section-wrapper">
        <div class="header-wrapper">
          <div>
            ${headerIconRenderer(this.data.source)}
          </div>
          <div class="flex-1">
            <div class="d-flex align-items-center">
              <gz-title fontSize="${FontSizes.HEADING_5}">
                ${sourceResourceNameRenderer(this.data)}
              </gz-title>
              <gz-copy-to-clipboard
                class="copy-icon"
                confirmationMessage="${translateFn('Copied')}"
                tooltipText="${translateFn('Copy to clipboard')}"
                clipboardMessage="${sourceResourceNameRenderer(this.data)}"
              ></gz-copy-to-clipboard>
            </div>
            <gz-text
              class="header-type"
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
            >
              ${sourceResourceTypeRenderer(this.data)}
            </gz-text>
          </div>
        </div>
        <div class="header-wrapper">
          <div>
            ${headerIconRenderer(this.data.target)}
          </div>
          <div class="flex-1">
            <div class="d-flex align-items-center">
              <gz-title fontSize="${FontSizes.HEADING_5}">
                ${targetResourceNameRenderer(this.data)}
              </gz-title>
              <gz-copy-to-clipboard
                class="copy-icon"
                confirmationMessage="${translateFn('Copied')}"
                tooltipText="${translateFn('Copy to clipboard')}"
                clipboardMessage="${targetResourceNameRenderer(this.data)}"
              ></gz-copy-to-clipboard>
            </div>
            <gz-text
              class="header-type"
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
            >
              ${targetResourceTypeRenderer(this.data)}
            </gz-text>
          </div>
        </div>
      </div>
    `;
  }

  private renderPublicRisksSection(): TemplateResult {
    return html`
      <div class="section py-2" data-section="public-risks">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="section-title text-uppercase mb-2"
        >
          ${translateFn(`Public facing risks (${1})`)}
        </gz-title>
        <gz-accordion
          accordionTitle="${'Allow all traffic by Network ACL'}"
          align="left"
        >
          <div class="pb-2 pt-1">
            <gz-text
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
            >
              ${'A Network ACL associated with Subnet allows all traffic.'}
            </gz-text>
            <div class="pt-2">
              <gz-labeled-content label="${translateFn('Resource type')}">
                ${'Network ACL'}
              </gz-labeled-content>
              <gz-labeled-content label="${translateFn('Resource name')}">
                ${this.renderLabeledContentWithEllipsisCopy('nacl-name-goes-here')}
              </gz-labeled-content>
              <gz-labeled-content label="${translateFn('Resource ID')}">
                ${this.renderLabeledContentWithEllipsisCopy('nacl-0a1b2c3d4e5f67890')}
              </gz-labeled-content>
              <gz-labeled-content label="${translateFn('Risk mitigation')}">
              </gz-labeled-content>
              <gz-text
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
              >
                ${'Lorem ipsum dolor sit amet, consectetur adipiscing elit'}
              </gz-text>
            </div>
          </div>
        </gz-accordion>
      </div>
    `;
  }

  private renderLabeledContentWithEllipsisCopy(value: string): TemplateResult {
    return html`
      <div class="w-full d-flex align-items-center copy-labeled-content">
        <gz-tooltip
          class="flex-1"
          isEllipsis
          tooltipText="${value}"
        >
          <span>${value}</span>
        </gz-tooltip>
        <gz-copy-to-clipboard
          class="copy-icon"
          confirmationMessage="${translateFn('Copied')}"
          tooltipText="${translateFn('Copy to clipboard')}"
          clipboardMessage="${value}"
        ></gz-copy-to-clipboard>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'breach-path-details-transition-side-menu': BreachPathDetailsTransitionSideMenuWebComponent;
  }
}
